import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Api from '../../services/api';
import { Operator, VehicleOperator } from '../../types/api.types';

interface OperatorInfoState {
  operators: Operator[];
  selectedOperator: Operator | null;
  operatorKnown: boolean;
  canPickOperator: boolean;
  operatorInjured: boolean | null;
  fetching: boolean;
  error: string | null;
}

const initialState: OperatorInfoState = {
  operators: [],
  selectedOperator: null,
  operatorKnown: true,
  canPickOperator: true,
  operatorInjured: null,
  fetching: false,
  error: null
};

// Async thunks
export const fetchOperators = createAsyncThunk(
  'operatorInfo/fetchOperators',
  async () => {
    const operators = await Api.getOperators();
    return operators;
  }
);

const operatorInfoSlice = createSlice({
  name: 'operatorInfo',
  initialState,
  reducers: {
    selectOperator: (state, action: PayloadAction<Operator | null>) => {
      state.selectedOperator = action.payload;
      state.error = null;
    },
    setOperatorKnown: (state, action: PayloadAction<boolean>) => {
      state.operatorKnown = action.payload;
    },
    setOperatorInjured: (state, action: PayloadAction<boolean>) => {
      state.operatorInjured = action.payload;
    },
    setOperators: (state, action: PayloadAction<Operator[]>) => {
      state.operators = action.payload;
    },
    computeOperatorAvailability: (state, action: PayloadAction<{ userId: string; vehicleOperators: VehicleOperator[] }>) => {
      const { userId, vehicleOperators } = action.payload;
      const vehicleOperatorIds = vehicleOperators.map(vo => vo.operatorId);
      const allAvailable = [
        ...state.operators.filter(o => vehicleOperatorIds.includes(o.id) || o.id === userId),
      ];
      state.canPickOperator = allAvailable.length > 0;
      state.operatorKnown = allAvailable.length > 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOperators.pending, (state) => {
      state.fetching = true;
      state.error = null;
    });
    builder.addCase(fetchOperators.fulfilled, (state, action) => {
      state.operators = action.payload;
      state.fetching = false;
      state.error = null;
    });
    builder.addCase(fetchOperators.rejected, (state, action) => {
      state.fetching = false;
      state.error = action.error.message || 'Failed to fetch operators';
    });
  }
});

export const {
  selectOperator,
  setOperatorKnown,
  setOperatorInjured,
  setOperators,
  computeOperatorAvailability,
} = operatorInfoSlice.actions;

export default operatorInfoSlice.reducer;

// Helper functions
export const getOperatorDisplayName = (operator: Operator): string => {
  return `${operator.firstName} ${operator.lastName}`.trim();
};

export const isOperatorInfoComplete = (state: OperatorInfoState): boolean => {
  if (!state.operatorKnown) {
    return state.operatorInjured !== null;
  }
  return state.selectedOperator !== null && state.operatorInjured !== null;
};

export const findOperatorByEmail = (
  operators: Operator[],
  email: string
): Operator | undefined => {
  return operators.find(op => op.email.toLowerCase() === email.toLowerCase());
};